import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  api,
  LeaderVerificationLeadersRow,
  MyLeaderVerificationRow,
} from '../../services/api';

type TabValue = 'mine' | 'leaders';

interface LeaderVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  /** True when the user holds a qualifying leader designation — unlocks the "My Actions" tab. */
  isLeader?: boolean;
  /** True for admins — unlocks the "Leaders Taken Action" tracking tab. */
  isAdmin?: boolean;
  /** Tab to show when the dialog opens; falls back to the default when the user lacks access to it. */
  initialTab?: TabValue;
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

const stickyTableContainerSx = {
  maxHeight: 'min(52vh, 560px)',
  overflow: 'auto',
} as const;

const stickyHeadCellSx = {
  /** Opaque thead color so scrolling body rows never show through the sticky header */
  backgroundColor: 'var(--color-surface-thead)',
  color: 'var(--color-text-strong)',
  fontWeight: 600,
  whiteSpace: 'nowrap' as const,
  zIndex: 3,
  boxShadow: 'inset 0 -1px 0 rgba(var(--color-border-rgb), 0.12)',
};

/**
 * "Leader Verifications" — eligible district directors (and admins) tick off
 * leadership actions (e.g. Share Distinctives)
 * once taken via the "My Actions" tab. The "Leaders Taken Action" tracking tab
 * lists, per action, every leader who has taken it, and is admin-only.
 * CPD Analytics lives in its own sidebar entry (see CpdAnalyticsDialog).
 */
const LeaderVerificationDialog: React.FC<LeaderVerificationDialogProps> = ({
  open,
  onClose,
  isLeader = true,
  isAdmin = false,
  initialTab,
}) => {
  // Both leaders and admins can act on their own actions ("My Actions" tab).
  const canAct = isLeader || isAdmin;
  const defaultTab: TabValue = canAct ? 'mine' : 'leaders';
  const [tab, setTab] = useState<TabValue>(defaultTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [myRows, setMyRows] = useState<MyLeaderVerificationRow[]>([]);
  const [leaderRows, setLeaderRows] = useState<LeaderVerificationLeadersRow[]>([]);
  const [drafts, setDrafts] = useState<Record<number, boolean>>({});
  const [submittingActionId, setSubmittingActionId] = useState<number | null>(null);

  const loadMine = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getMyLeaderVerifications();
      setMyRows(response.actions);
      const nextDrafts: Record<number, boolean> = {};
      for (const row of response.actions) {
        nextDrafts[row.actionId] = row.myVerification?.takenAction === true;
      }
      setDrafts(nextDrafts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load leader verifications');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLeaders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getLeaderVerificationLeaders();
      setLeaderRows(response.actions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load leaders');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshActiveTab = useCallback(() => {
    if (tab === 'leaders') loadLeaders();
    else loadMine();
  }, [tab, loadMine, loadLeaders]);

  useEffect(() => {
    if (!open) return;
    // Honor a requested initial tab only when the user can access it.
    if (initialTab === 'leaders' && isAdmin) setTab('leaders');
    else if (initialTab === 'mine' && canAct) setTab('mine');
    else setTab(defaultTab);
  }, [open, initialTab, isAdmin, canAct, defaultTab]);

  // Keep the active tab on one the user can actually see: users who can't act
  // can't stay on "My Actions"; non-admins can't stay on "Leaders Taken Action".
  useEffect(() => {
    if (tab === 'mine' && !canAct) setTab('leaders');
    else if (tab === 'leaders' && !isAdmin) setTab('mine');
  }, [tab, canAct, isAdmin]);

  useEffect(() => {
    if (!open) return;
    if (tab === 'leaders' && isAdmin) loadLeaders();
    else loadMine();
  }, [open, tab, isAdmin, loadMine, loadLeaders]);

  const handleSubmit = async (actionId: number) => {
    setSubmittingActionId(actionId);
    setError(null);
    try {
      await api.submitLeaderVerification({
        actionId,
        takenAction: drafts[actionId] === true,
      });
      await loadMine();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit leader verification');
    } finally {
      setSubmittingActionId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
          minHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h6">Leader Verifications</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
              {canAct
                ? 'Tick each leadership action once you have taken it.'
                : 'Track which leaders have taken each action.'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={refreshActiveTab} size="small" disabled={loading} title="Refresh">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          {canAct && <Tab value="mine" label="My Actions" />}
          {isAdmin && <Tab value="leaders" label="Leaders Taken Action" />}
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : tab === 'leaders' && isAdmin ? (
          <LeadersTable rows={leaderRows} />
        ) : (
          <MyActionsTable
            rows={myRows}
            drafts={drafts}
            submittingActionId={submittingActionId}
            onDraftChange={(actionId, checked) =>
              setDrafts((prev) => ({ ...prev, [actionId]: checked }))
            }
            onSubmit={handleSubmit}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

interface MyActionsTableProps {
  rows: MyLeaderVerificationRow[];
  drafts: Record<number, boolean>;
  submittingActionId: number | null;
  onDraftChange: (actionId: number, checked: boolean) => void;
  onSubmit: (actionId: number) => void;
}

const MyActionsTable: React.FC<MyActionsTableProps> = ({
  rows,
  drafts,
  submittingActionId,
  onDraftChange,
  onSubmit,
}) => {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
        No leadership actions to verify yet.
      </Typography>
    );
  }
  return (
    <TableContainer sx={stickyTableContainerSx}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeadCellSx}>Action</TableCell>
            <TableCell sx={stickyHeadCellSx} align="center">
              Taken Action
            </TableCell>
            <TableCell sx={stickyHeadCellSx}>Last Updated</TableCell>
            <TableCell sx={stickyHeadCellSx} align="right">
              Action
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const submitting = submittingActionId === row.actionId;
            return (
              <TableRow key={row.actionId} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.label}
                  </Typography>
                  {row.description && (
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', display: 'block' }}>
                      {row.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={drafts[row.actionId] === true}
                    onChange={(e) => onDraftChange(row.actionId, e.target.checked)}
                    disabled={submitting}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {formatDateTime(row.myVerification?.verifiedAt || null)}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onSubmit(row.actionId)}
                    disabled={submitting}
                  >
                    {submitting ? 'Saving…' : 'Submit'}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const LeadersTable: React.FC<{ rows: LeaderVerificationLeadersRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
        No leadership actions defined yet.
      </Typography>
    );
  }
  return (
    <TableContainer sx={stickyTableContainerSx}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeadCellSx}>Action</TableCell>
            <TableCell sx={stickyHeadCellSx}>Leader</TableCell>
            <TableCell sx={stickyHeadCellSx}>CEA</TableCell>
            <TableCell sx={stickyHeadCellSx}>Mobile</TableCell>
            <TableCell sx={stickyHeadCellSx}>Designation</TableCell>
            <TableCell sx={stickyHeadCellSx}>Taken At</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((action) =>
            action.leaders.length === 0 ? (
              <TableRow key={`${action.actionId}-empty`}>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{action.label}</TableCell>
                <TableCell colSpan={5} sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', fontStyle: 'italic' }}>
                  No leader has taken this action yet
                </TableCell>
              </TableRow>
            ) : (
              action.leaders.map((leader, index) => (
                <TableRow key={`${action.actionId}-${leader.id}`} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: index === 0 ? 600 : 400 }}>
                    {index === 0 ? action.label : ''}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'var(--color-success)' }} />
                      <Typography variant="body2">
                        {leader.userName || leader.ceaNumber || leader.userId}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{leader.ceaNumber || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{leader.mobile || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{leader.designation || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leader.verifiedAt)}</TableCell>
                </TableRow>
              ))
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LeaderVerificationDialog;
